import type { CityData } from "@/lib/cities-data.types";

export type TithiLanguage = "en" | "hi";
export type TithiPurposeId = "puja" | "travel" | "interview_work" | "purchase_new_beginning";

export interface TithiPurposeOption {
  id: TithiPurposeId;
  label: string;
  description: string;
}

export interface TithiPurposeFact {
  label: string;
  value: string;
}

export const TITHI_DEFAULT_CITY: CityData = {
  slug: "new-delhi",
  name: "New Delhi",
  state: "Delhi",
  latitude: 28.6139,
  longitude: 77.209,
  timezone: "Asia/Kolkata",
};

const TITHI_NAMES_HI: Readonly<Record<string, string>> = {
  Pratipada: "प्रतिपदा",
  Dwitiya: "द्वितीया",
  Tritiya: "तृतीया",
  Chaturthi: "चतुर्थी",
  Panchami: "पंचमी",
  Shashthi: "षष्ठी",
  Shashti: "षष्ठी",
  Saptami: "सप्तमी",
  Ashtami: "अष्टमी",
  Navami: "नवमी",
  Dashami: "दशमी",
  Ekadashi: "एकादशी",
  Dwadashi: "द्वादशी",
  Trayodashi: "त्रयोदशी",
  Chaturdashi: "चतुर्दशी",
  Purnima: "पूर्णिमा",
  Amavasya: "अमावस्या",
};

export const TITHI_PURPOSES: Readonly<Record<TithiLanguage, readonly TithiPurposeOption[]>> = {
  en: [
    { id: "puja", label: "Puja", description: "Prayer, sankalp, or a home ritual" },
    { id: "travel", label: "Travel", description: "A journey, departure, or important visit" },
    {
      id: "interview_work",
      label: "Interview / work",
      description: "An interview, pitch, decision, filing, or key work task",
    },
    {
      id: "purchase_new_beginning",
      label: "Purchase / new beginning",
      description: "A valuable purchase, commitment, venture, role, or new chapter",
    },
  ],
  hi: [
    { id: "puja", label: "पूजा", description: "पूजा, संकल्प या घर का धार्मिक कार्य" },
    { id: "travel", label: "यात्रा", description: "यात्रा, प्रस्थान या महत्वपूर्ण भेंट" },
    {
      id: "interview_work",
      label: "साक्षात्कार / काम",
      description: "साक्षात्कार, प्रस्तुति, निर्णय, आवेदन या मुख्य कार्य",
    },
    {
      id: "purchase_new_beginning",
      label: "खरीद / नई शुरुआत",
      description: "कीमती खरीद, आर्थिक वचन, व्यवसाय, भूमिका या नया अध्याय",
    },
  ],
};

export const TITHI_COPY = {
  en: {
    headerTitle: "Aaj Ki Tithi",
    headerSubtitle: "Today's Hindu lunar day",
    cityLabel: "Your city",
    cityPlaceholder: "Search your city",
    cityHelp: "Tithi boundaries and daily timings are calculated for this location.",
    loading: "Calculating today's tithi for your city…",
    refreshing: "Updating the engine result for this city…",
    error: "Today's tithi could not be calculated. Please try again.",
    retry: "Try again",
    patternTitle: "Today's Tithi pattern",
    significanceTitle: "Significance",
    auspiciousForNewWork: "Auspicious for new work",
    auspiciousYes: "Yes — this tithi is favorable for starting new activities",
    auspiciousNo: "No — this tithi is better for worship and spiritual practice",
    snapshotTitle: "Today's Panchang snapshot",
    fullPanchang: "See full Panchang",
    tithi: "Tithi",
    nakshatra: "Nakshatra",
    yoga: "Yoga",
    karana: "Karana",
    vara: "Vara (day)",
    sunrise: "Sunrise",
    purposeTitle: "What are you checking today's timing for?",
    purposeHint: "Choose one purpose to see the relevant engine anchors.",
    previewEyebrow: "Engine-backed preview",
    previewTitle: "Today's timing anchors",
    previewIntro: "These are the Panchang facts calculated for your selected city and purpose.",
    previewBoundary:
      "This is a public daily snapshot, not a personal Muhurat verdict. A personal window also needs your birth chart and the exact purpose.",
    choosePurpose: "Choose one purpose above to build today's preview.",
    previewUpdating: "Recalculating the preview for your new city…",
    notAvailable: "Not returned by today's engine result",
    detected: "Detected by the engine",
    notDetected: "Not detected by the engine",
    brahmaMuhurat: "Brahma Muhurat",
    rahuKaal: "Rahu Kaal",
    sunset: "Sunset",
    moonRashi: "Moon sign",
    abhijitMuhurat: "Abhijit Muhurat",
    amritKaal: "Amrit Kaal",
    sarvarthaSiddhi: "Sarvartha Siddhi Yoga",
  },
  hi: {
    headerTitle: "आज की तिथि",
    headerSubtitle: "आज का हिंदू चंद्र दिवस",
    cityLabel: "आपका शहर",
    cityPlaceholder: "अपना शहर खोजें",
    cityHelp: "तिथि की सीमा और दैनिक समय इसी स्थान के अनुसार निकाले जाते हैं।",
    loading: "आपके शहर के लिए आज की तिथि की गणना हो रही है…",
    refreshing: "इस शहर के लिए इंजन का परिणाम बदला जा रहा है…",
    error: "आज की तिथि की गणना नहीं हो सकी। कृपया फिर प्रयास करें।",
    retry: "फिर प्रयास करें",
    patternTitle: "आज की तिथि का प्रकार",
    significanceTitle: "महत्व",
    auspiciousForNewWork: "नए कार्य के लिए शुभ",
    auspiciousYes: "हाँ — यह तिथि नई गतिविधि शुरू करने के लिए अनुकूल मानी जाती है",
    auspiciousNo: "नहीं — यह तिथि पूजा और आध्यात्मिक साधना के लिए अधिक उपयुक्त मानी जाती है",
    snapshotTitle: "आज के पंचांग का सार",
    fullPanchang: "पूरा पंचांग देखें",
    tithi: "तिथि",
    nakshatra: "नक्षत्र",
    yoga: "योग",
    karana: "करण",
    vara: "वार",
    sunrise: "सूर्योदय",
    purposeTitle: "आप आज का समय किस काम के लिए देख रहे हैं?",
    purposeHint: "आज के संबंधित पंचांग आधार देखने के लिए एक उद्देश्य चुनें।",
    previewEyebrow: "इंजन-आधारित झलक",
    previewTitle: "आज के समय के पंचांग आधार",
    previewIntro: "ये तथ्य आपके चुने हुए शहर और उद्देश्य के लिए पंचांग इंजन ने निकाले हैं।",
    previewBoundary:
      "यह सार्वजनिक दैनिक सार है, व्यक्तिगत मुहूर्त का निर्णय नहीं। व्यक्तिगत समय के लिए आपकी जन्म कुंडली और सटीक उद्देश्य भी आवश्यक हैं।",
    choosePurpose: "आज की झलक बनाने के लिए ऊपर एक उद्देश्य चुनें।",
    previewUpdating: "आपके नए शहर के लिए झलक की दोबारा गणना हो रही है…",
    notAvailable: "आज के इंजन परिणाम में उपलब्ध नहीं",
    detected: "इंजन ने पाया",
    notDetected: "इंजन ने नहीं पाया",
    brahmaMuhurat: "ब्रह्म मुहूर्त",
    rahuKaal: "राहु काल",
    sunset: "सूर्यास्त",
    moonRashi: "चंद्र राशि",
    abhijitMuhurat: "अभिजित मुहूर्त",
    amritKaal: "अमृत काल",
    sarvarthaSiddhi: "सर्वार्थ सिद्धि योग",
  },
} as const;

type PanchangResult = import("@/lib/api/panchang").PanchangResponse;

function periodValue(
  period: { start: string; end: string } | null | undefined,
  language: TithiLanguage
): string {
  return period ? `${period.start}–${period.end}` : TITHI_COPY[language].notAvailable;
}

/** Purpose-specific facts only; labels and values always come from the engine result. */
export function buildTithiPurposeFacts(
  purpose: TithiPurposeId,
  result: PanchangResult,
  language: TithiLanguage
): readonly TithiPurposeFact[] {
  const copy = TITHI_COPY[language];
  const tithi = `${displayTithiName(result.tithi.name, language)} · ${displayPakshaName(
    result.tithi.paksha,
    language
  )}`;
  const purposeFacts: Record<TithiPurposeId, readonly TithiPurposeFact[]> = {
    puja: [
      { label: copy.tithi, value: tithi },
      { label: copy.nakshatra, value: result.nakshatra.name },
      { label: copy.brahmaMuhurat, value: periodValue(result.brahma_muhurat, language) },
      { label: copy.yoga, value: result.yoga.name },
    ],
    travel: [
      { label: copy.vara, value: displayVaraName(result.vara, language) },
      { label: copy.moonRashi, value: result.moon_rashi },
      { label: copy.rahuKaal, value: periodValue(result.rahu_kaal, language) },
      { label: copy.sunset, value: result.sunset },
    ],
    interview_work: [
      { label: copy.yoga, value: result.yoga.name },
      { label: copy.karana, value: result.karana.name },
      { label: copy.abhijitMuhurat, value: periodValue(result.abhijit_muhurta, language) },
      { label: copy.nakshatra, value: result.nakshatra.name },
    ],
    purchase_new_beginning: [
      { label: copy.tithi, value: tithi },
      { label: copy.karana, value: result.karana.name },
      { label: copy.amritKaal, value: periodValue(result.amrit_kaal, language) },
      {
        label: copy.sarvarthaSiddhi,
        value: result.auspicious.sarvartha_siddhi_yoga ? copy.detected : copy.notDetected,
      },
    ],
  };
  return purposeFacts[purpose];
}

export function displayTithiName(name: string, language: TithiLanguage): string {
  return language === "hi" ? (TITHI_NAMES_HI[name] ?? name) : name;
}

export function displayPakshaName(paksha: string, language: TithiLanguage): string {
  if (language === "en") return paksha;
  const normalized = paksha.toLowerCase();
  if (normalized.includes("shukla")) return "शुक्ल पक्ष";
  if (normalized.includes("krishna")) return "कृष्ण पक्ष";
  return paksha;
}

export function displayVaraName(
  vara: { name: string; name_hi?: string | null },
  language: TithiLanguage
): string {
  return language === "hi" && vara.name_hi ? vara.name_hi : vara.name;
}

export function tithiDateLabel(date: string, language: TithiLanguage): string {
  return new Intl.DateTimeFormat(language === "hi" ? "hi-IN" : "en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(`${date}T12:00:00+05:30`));
}
